import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, ChevronDown } from "lucide-react";
import EcosystemFlywheel from "../EcosystemFlywheel";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="section-hero"
      className="relative min-h-[100svh] pt-24 md:pt-28 pb-6 overflow-hidden flex flex-col"
    >
      {/* Layered background */}
      <div className="absolute inset-0 grid-bg grid-mask opacity-60 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/17194838/pexels-photo-17194838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage:
            "radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%)",
        }}
      />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-violet-600/10 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Top meta strip */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-between mb-12 md:mb-16"
        >
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
              F3 / Index · Q4 2025
            </span>
          </div>
          <div className="hidden md:block text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
            Premium Media · Operating System
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: Copy */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase">
                The Ecosystem of Intent
              </span>
              <span className="hr-line w-16" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0]"
            >
              A premium, AI-powered destination for news, knowledge and current
              affairs
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-8 text-zinc-400 text-base md:text-lg max-w-2xl leading-relaxed"
            >
              A professional alternative to mass-market video platforms — built
              around <span className="text-white">value density</span>,{" "}
              <span className="text-white">directed intent</span> and{" "}
              <span className="text-white">high-integrity content</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <a
                href="#platform"
                data-testid="hero-cta-explore"
                className="group inline-flex items-center gap-2 bg-violet-700 text-white px-6 py-3.5 text-sm font-medium hover:bg-violet-600 transition-colors"
              >
                Explore Platform
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
              </a>
              <a
                href="#why-f3"
                data-testid="hero-cta-why"
                className="group inline-flex items-center gap-2 border border-white/15 text-white px-6 py-3.5 text-sm hover:border-white/40 hover:bg-white/5 transition-all"
              >
                Why F3
                <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2} />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-partner"
                className="group inline-flex items-center gap-2 text-zinc-400 px-3 py-3.5 text-sm hover:text-white transition-colors"
              >
                Partner With Us
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
              </a>
            </motion.div>
          </div>

          {/* Right: Flywheel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.4 }}
            className="lg:col-span-6"
          >
            <EcosystemFlywheel />
          </motion.div>
        </div>
      </div>

      {/* Know More scroll cue pinned to bottom of hero viewport */}
      <motion.a
        href="#why-f3"
        data-testid="hero-know-more"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.8 }}
        className="group mt-auto mx-auto flex flex-col items-center gap-2 pb-6 text-zinc-400 hover:text-white transition-colors"
      >
        <span className="text-[11px] font-mono uppercase tracking-[0.3em]">
          Know more
        </span>
        <ChevronDown className="w-5 h-5 animate-bounce" strokeWidth={1.5} />
      </motion.a>
    </section>
  );
}
